import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSmartBack } from "@/lib/navigation-utils";
import {
  ArrowLeft,
  User,
  Weight,
  Phone,
  Mail,
  MapPin,
  AlertTriangle,
  Pencil, X,
} from "lucide-react";
import { Button } from "@akuna/ui";
import { AddressLink } from "@/components/shared/AddressLink";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePet } from "@/hooks/usePets";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { useMedicalRecords } from "@/hooks/useMedicalRecords";
import PatientHistoryTimeline from "@/components/patients/PatientHistoryTimeline";
import { useQueryClient } from "@tanstack/react-query";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useProfile } from "@/hooks/useProfile";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { formatDateBR, formatPetAge } from "@/lib/date-utils";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { ProfileDataModal } from "@/components/prescriptions/ProfileDataModal";
import { PetWeightHistoryCard, type PetWeightWithMeta } from "@/components/pets/PetWeightHistoryCard";
import { getLatestPetWeight, getActivePetWeights } from "@/lib/utils/pet-weight";
import { PatientFutureTab } from "@/components/patients/PatientFutureTab";
import { PatientActionsMenu } from "@/components/patients/PatientActionsMenu";
import type { PatientDayEventType } from "@/components/calendar/types/patient-day-events";
import { PatientPackagesPanel } from "@/components/packages/PatientPackagesPanel";
import { PatientFinancialTab } from "@/components/patients/PatientFinancialTab";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { getPetDeathSummary, isPetDeceased } from "@/lib/utils/pet-death";
import { QuickEditPetDialog } from "@/components/pets/QuickEditPetDialog";
import { MobilePetProfileShell, type PetProfileTab as MobilePetProfileTabType } from "@/components/pets/mobile/MobilePetProfileShell";
import { MobilePetOverviewTab } from "@/components/pets/mobile/MobilePetOverviewTab";
import { PetDaycareHotelSummary } from "@/components/pets/daycare-hotel/PetDaycareHotelSummary";
import { MobilePetHistoryTab } from "@/components/pets/mobile/MobilePetHistoryTab";
import { MobilePetCareTab } from "@/components/pets/mobile/MobilePetCareTab";
import { MobilePetPackagesTab } from "@/components/pets/mobile/MobilePetPackagesTab";
import { MobilePetFinancialTab } from "@/components/pets/mobile/MobilePetFinancialTab";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import {IconButton} from "@akuna/ui";


const formatDate = (value?: string | null) => {
  return formatDateBR(value);
};

type PetProfileTab = "overview" | "history" | "upcoming" | "packages" | "financial";

const PET_PROFILE_TAB_BY_ROUTE: Record<string, PetProfileTab> = {
  overview: "overview",
  history: "history",
  upcoming: "upcoming",
  packages: "packages",
  financial: "financial",
  basicas: "overview",
  historico: "history",
  futuro: "upcoming",
  pacotes: "packages",
  financeiro: "financial",
  consultas: "history",
};

const ViewPet = () => {
  const { t } = useTranslation(["patients", "common", "calendar"]);
  const navigate = useNavigate();
  const location = useLocation();
  const { id, petTab } = useParams<{ id: string; petTab?: string }>();
  const smartBack = useSmartBack();
  const locationState = (location.state as {
    activeTab?: string;
    openHistoryEvent?: { type?: PatientDayEventType; sourceId?: string };
  } | null) ?? null;
  const routeTab = useMemo<PetProfileTab | null>(() => {
    if (!petTab) return null;
    return PET_PROFILE_TAB_BY_ROUTE[petTab] ?? null;
  }, [petTab]);
  const stateTab = useMemo<PetProfileTab | null>(() => {
    const fromState = locationState?.activeTab;
    if (!fromState) return null;
    return PET_PROFILE_TAB_BY_ROUTE[fromState] ?? "history";
  }, [locationState?.activeTab]);
  const initialTab = routeTab ?? stateTab ?? "overview";
  const [activeTab, setActiveTab] = useState<PetProfileTab>(initialTab);
  const [isDeceasedStartConsultOpen, setIsDeceasedStartConsultOpen] = useState(false);
  const initialOpenHistoryEvent = useMemo(() => {
    const candidate = locationState?.openHistoryEvent;
    if (!candidate?.sourceId || !candidate?.type) return undefined;

    const allowedTypes: PatientDayEventType[] = [
      "consultation",
      "vaccine",
      "exam",
      "prescription",
      "grooming",
    ];
    if (!allowedTypes.includes(candidate.type)) return undefined;

    return {
      type: candidate.type,
      sourceId: candidate.sourceId,
    };
  }, [locationState?.openHistoryEvent]);
  const [showProfileDataModal, setShowProfileDataModal] = useState(false);
  const [pendingStartConsult, setPendingStartConsult] = useState(false);
  const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);
  const { data: pet, isLoading, error } = usePet(id ?? "");
  const { hasPermission: canViewPatientFinancial } = useHasPermissionSync("patient_financial", "view");
  const { hasDaycare } = useDaycareHotelAccess();

  const { updateMedicalRecord } = useMedicalRecords(id);
  const { data: entitlements } = useEntitlements();

  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!id || routeTab) return;
    navigate(`/patients/pets/${id}/${initialTab}`, {
      replace: true,
      state: location.state,
    });
  }, [id, initialTab, location.state, navigate, routeTab]);

  useEffect(() => {
    if (!id) return;
    if (routeTab !== "financial") return;
    if (canViewPatientFinancial) return;

    navigate(`/patients/pets/${id}/overview`, {
      replace: true,
      state: location.state,
    });
  }, [canViewPatientFinancial, id, location.state, navigate, routeTab]);

  const hasCRMV = Boolean(
    profile?.crmv_state &&
    profile.crmv_state.trim() !== "" &&
    profile?.crmv_number &&
    profile.crmv_number > 0
  );
  const clinicId = profile?.current_clinic_id ?? null;
  const ownerId = pet?.owner?.id ?? null;
  const canStartConsult = Boolean(pet?.id && profile?.current_clinic_id);
  const isDeceasedPet = isPetDeceased(pet);

  const handleStartConsultAction = () => {
    if (!pet?.id || !canStartConsult) return;
    if (!hasCRMV) {
      setPendingStartConsult(true);
      setShowProfileDataModal(true);
      return;
    }
    if (isDeceasedPet) {
      setIsDeceasedStartConsultOpen(true);
      return;
    }
    navigate(`/patients/pets/${pet.id}/medical-records/new`);
  };

  const handleProfileDataComplete = async () => {
    setShowProfileDataModal(false);
    await queryClient.refetchQueries({ queryKey: ["profile"] });
    const updatedProfile = queryClient.getQueryData<{
      crmv_state?: string | null;
      crmv_number?: number | null;
    }>(["profile"]);
    const hasCrmv = Boolean(
      updatedProfile?.crmv_state &&
      updatedProfile.crmv_state.trim() !== "" &&
      updatedProfile?.crmv_number &&
      updatedProfile.crmv_number > 0
    );

    if (pendingStartConsult && hasCrmv && pet?.id) {
      setPendingStartConsult(false);
      if (isDeceasedPet) {
        setIsDeceasedStartConsultOpen(true);
      } else {
        navigate(`/patients/pets/${pet.id}/medical-records/new`);
      }
    } else {
      setPendingStartConsult(false);
    }
  };

  type OwnerPhone = Pick<Tables<"owners_phones">, "phone_number" | "is_primary" | "is_whatsapp">;
  type OwnerEmail = Pick<Tables<"owners_emails">, "email" | "is_primary">;
  type OwnerAddress = Tables<"owners_addresses">;
  type OwnerAddressWithCity = OwnerAddress & {
    city?: { name?: string | null; state?: { name?: string | null } | null } | null;
  };
  type PetWeight = PetWeightWithMeta;
  type PetAttachment = Tables<"pet_attachments"> & {
    storage_path?: string | null;
    signed_url?: string | null;
  };
  type PetTemperament = { id: string; condition?: { id: string; name: string; type: string } | null };
  type PetAllergy = { id: string; condition?: { id: string; name: string; type: string } | null };

  const age = useMemo(() => {
    return formatPetAge(pet?.birth_date);
  }, [pet?.birth_date]);

  const weightChartData = useMemo(() => {
    const weights = getActivePetWeights((pet?.weights ?? []) as PetWeight[]);
    const ascending = [...weights]
      .filter((w) => Boolean(w.measured_at) && typeof w.weight === "number")
      .sort(
        (a, b) =>
          new Date(a.measured_at as string).getTime() - new Date(b.measured_at as string).getTime(),
      );

    return ascending.map((w) => ({
      date: format(new Date(w.measured_at as string), "dd/MM/yy", { locale: ptBR }),
      weight: Number(w.weight),
    }));
  }, [pet?.weights]);

  if (!id) {
    return (
      <PermissionGuard resource="patients" action="view">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold text-foreground">{t("petProfile.notFound")}</h1>
          </div>
        </div>
      </PermissionGuard>
    );
  }

  if (isLoading) {
    return (
      <PermissionGuard resource="patients" action="view">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Skeleton className="h-8 w-48" />
          </div>
          <Skeleton className="h-12 w-full" />
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </PermissionGuard>
    );
  }

  if (error || !pet) {
    return (
      <PermissionGuard resource="patients" action="view">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold text-foreground">{t("petProfile.loadErrorTitle")}</h1>
          </div>
          <Card>
            <CardContent className="py-6">
              <p className="text-muted-foreground">
                {t("petProfile.loadErrorDescription")}
              </p>
              <Button className="mt-4" onClick={() => smartBack()}>
                {t("ownerProfile.backToPatients")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </PermissionGuard>
    );
  }

  const ownerPhones = (pet.owner?.phones ?? []) as OwnerPhone[];
  const ownerEmails = (pet.owner?.emails ?? []) as OwnerEmail[];
  const ownerAddresses = (pet.owner?.addresses ?? []) as OwnerAddressWithCity[];
  const petWeights = (pet.weights ?? []) as PetWeight[];
  const petAttachments = ((pet.attachments ?? []) as PetAttachment[]).filter(
    (attachment) => !attachment.deleted_at
  );
  const petAllergies = (pet.allergies ?? []) as PetAllergy[];
  const petTemperaments = (pet.temperaments ?? []) as PetTemperament[];

  const ownerPrimaryPhone = ownerPhones.find((phone) => phone.is_primary);
  const ownerPrimaryEmail = ownerEmails.find((email) => email.is_primary);

  const showGrooming = entitlements?.activeModules?.includes("grooming");
  // Packages tab covers both grooming kit/combo packages and daycare/hotel stay
  // plans (PatientPackagesPanel renders StayPlansSection internally), so it needs
  // either module to be worth showing.
  const canViewPackagesTab = Boolean(showGrooming) || hasDaycare;
  const resolvedActiveTab =
    (canViewPatientFinancial || activeTab !== "financial") && (canViewPackagesTab || activeTab !== "packages")
      ? activeTab
      : "overview";

  const lastWeight = getLatestPetWeight(petWeights);
  const currentWeightValue = lastWeight?.weight ?? null;
  const hasWeightSummary = Boolean(currentWeightValue || lastWeight?.measured_at || weightChartData.length > 0);
  const petPhotoPath = Array.isArray(pet.photo)
    ? pet.photo[0]?.file_path
    : pet.photo?.file_path;
  const hasPhoto = Boolean(petPhotoPath);
  const petLookups = pet as typeof pet & {
    color?: { name: string } | null;
    coat_type?: { name: string } | null;
    size_category?: { name: string } | null;
  };
  const deathSummary = getPetDeathSummary(pet);
  const overviewFields = [
    pet.status?.name
      ? {
        label: t("petProfile.fields.status"),
        value: pet.status.name,
      }
      : null,
    deathSummary
      ? {
        label: t("petProfile.fields.deathSummary"),
        value: `${formatDate(deathSummary.deathDate)}${deathSummary.deathCause ? ` • ${deathSummary.deathCause}` : ""}`,
      }
      : null,
    pet.record_number
      ? {
        label: t("petProfile.fields.recordNumber"),
        value: pet.record_number,
      }
      : null,
    pet.gender?.name
      ? {
        label: t("petProfile.fields.gender"),
        value: pet.gender.name,
      }
      : null,
    pet.species?.name
      ? {
        label: t("petProfile.fields.species"),
        value: pet.species.name,
      }
      : null,
    pet.breed?.name
      ? {
        label: t("petProfile.fields.breed"),
        value: pet.breed.name,
      }
      : null,
    petLookups.size_category?.name
      ? {
        label: t("petProfile.fields.sizeCategory"),
        value: petLookups.size_category.name,
      }
      : null,
    (pet.birth_date || age)
      ? {
        label: t("petProfile.fields.age"),
        value: [formatDate(pet.birth_date), age].filter(Boolean).join(" • "),
      }
      : null,
    pet.sterilization?.name
      ? {
        label: t("petProfile.fields.sterilization"),
        value: pet.sterilization.name,
      }
      : null,
    (petLookups.color?.name || petLookups.coat_type?.name)
      ? {
        label: t("petProfile.fields.colorCoat"),
        value: [petLookups.color?.name, petLookups.coat_type?.name].filter(Boolean).join(" • "),
      }
      : null,
  ].filter((item): item is { label: string; value: string } => Boolean(item?.value));
  const fullWidthFields = [
    pet.health_plan
      ? {
        label: t("petProfile.fields.healthPlan"),
        value: pet.health_plan,
      }
      : null,
  ].filter((item): item is { label: string; value: string } => Boolean(item?.value));

  const handleClearConsultationWeight = async (medicalRecordId: string) => {
    try {
      await updateMedicalRecord({
        id: medicalRecordId,
        data: { weight: null },
      });
    } catch (error) {
      console.error("Erro ao remover peso da consulta:", error);
    }
  };

  // ── Mobile helpers ──
  const profileInitials = (() => {
    const name = profile?.full_name ?? "";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return "?";
  })();

  const petSubtitle = [
    pet.created_at ? t("petProfile.patientSince", { date: formatDate(pet.created_at) }) : null,
    pet.owner?.name ?? null,
  ].filter(Boolean).join(" · ");

  const mobileTabs: { value: MobilePetProfileTabType; label: string }[] = [
    { value: "overview", label: t("petProfile.tabs.basicInfo") },
    { value: "history", label: t("petProfile.tabs.history") },
    { value: "upcoming", label: t("petProfile.tabs.appointments") },
    ...(canViewPackagesTab
      ? [
          {
            value: "packages" as const,
            label: t(hasDaycare ? "petProfile.tabs.packagesWithPlans" : "petProfile.tabs.packages"),
          },
        ]
      : []),
    ...(canViewPatientFinancial ? [{ value: "financial" as const, label: t("petProfile.tabs.financial") }] : []),
  ];

  return (
    <PermissionGuard resource="patients" action="view">
      {/* ── MOBILE LAYOUT ── */}
      <div className="md:hidden h-full">
        <MobilePetProfileShell
          petName={pet.name}
          subtitleLine={petSubtitle}
          userInitials={profileInitials}
          activeTab={resolvedActiveTab as MobilePetProfileTabType}
          tabs={mobileTabs}
          onBack={() => smartBack()}
          backLabel={t("ownerProfile.backToPatients")}
          onTabChange={(tab) =>
            navigate(`/patients/pets/${pet.id}/${tab}`, { replace: true, state: location.state })
          }
          actionsButton={
            <PatientActionsMenu
              petId={pet.id}
              ownerId={ownerId}
              clinicId={clinicId}
              speciesId={pet.species_id}
              canStartConsult={canStartConsult}
              onStartConsult={handleStartConsultAction}
              compactTrigger
              triggerClassName="fixed bottom-[calc(96px+env(safe-area-inset-bottom))] right-4 z-40 shadow-xl shadow-primary/25 md:hidden"
            />
          }
        >
          {resolvedActiveTab === "overview" && (
            <MobilePetOverviewTab
              petId={pet.id}
              petName={pet.name}
              statusName={pet.status?.name ?? null}
              speciesName={pet.species?.name ?? null}
              genderName={pet.gender?.name ?? null}
              sizeCategoryName={petLookups.size_category?.name ?? null}
              ageLabel={age}
              recordNumber={pet.record_number ?? null}
              raceName={pet.breed?.name ?? null}
              birthDateLabel={pet.birth_date ? formatDate(pet.birth_date) : null}
              currentWeightValue={currentWeightValue}
              lastWeightDateLabel={lastWeight?.measured_at ? formatDate(lastWeight.measured_at) : null}
              weightChartData={weightChartData}
              petWeights={petWeights}
              clearConsultationWeight={handleClearConsultationWeight}
              ownerId={ownerId}
              ownerName={pet.owner?.name ?? null}
              ownerPrimaryPhone={ownerPrimaryPhone ?? null}
              ownerPrimaryEmail={ownerPrimaryEmail ?? null}
              ownerAddresses={ownerAddresses}
              petPhotoPath={petPhotoPath}
              editButton={
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 rounded-full bg-background-app"
                    onClick={() => setIsQuickEditOpen(true)}
                    aria-label={t("edit", { ns: "common" })}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <QuickEditPetDialog
                    petId={pet.id}
                    open={isQuickEditOpen}
                    onOpenChange={setIsQuickEditOpen}
                  />
                </>
              }
            />
          )}
          {resolvedActiveTab === "history" && (
            <MobilePetHistoryTab
              petId={pet.id}
              clinicId={clinicId}
              includeGrooming={showGrooming ?? false}
              initialOpenEvent={initialOpenHistoryEvent}
            />
          )}
          {resolvedActiveTab === "upcoming" && (
            <MobilePetCareTab
              petId={pet.id}
              ownerId={ownerId}
              clinicId={clinicId}
              includeGrooming={showGrooming ?? false}
            />
          )}
          {resolvedActiveTab === "packages" && canViewPackagesTab && (
            <MobilePetPackagesTab
              ownerId={ownerId}
              petId={pet.id}
              clinicId={clinicId}
              ownerName={pet.owner?.name ?? null}
              petName={pet.name}
            />
          )}
          {resolvedActiveTab === "financial" && canViewPatientFinancial && (
            <MobilePetFinancialTab petId={pet.id} />
          )}
        </MobilePetProfileShell>
      </div>

      {/* ── DESKTOP LAYOUT (unchanged) ── */}
      <div className="hidden md:block">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()} aria-label={t("ownerProfile.backToPatients")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{pet.name}</h1>
              <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                <span>{t("petProfile.patientSince", { date: formatDate(pet.created_at) })}</span>
                {pet.owner?.name ? (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-1 text-xs">
                      <User className="h-3 w-3" />
                      {t("petProfile.ownerLabel", { name: pet.owner.name })}
                    </span>
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <IconButton asChild size="sm" variant="outline" label={t("common:edit")}>
              <Link to={`/patients/pets/${pet.id}/edit`}>
                <Pencil aria-hidden="true" size={16} />
              </Link>
            </IconButton>
            <PatientActionsMenu
              petId={pet.id}
              ownerId={ownerId}
              clinicId={clinicId}
              speciesId={pet.species_id}
              canStartConsult={canStartConsult}
              onStartConsult={handleStartConsultAction}
            />
          </div>
        </div>

        <div className={hasPhoto ? "grid gap-6 md:grid-cols-[180px,1fr] lg:grid-cols-[320px,1fr]" : ""}>
          {/* Foto lateral */}
          {hasPhoto && (
            <div className="rounded-card bg-background-gradient p-4 shadow-soft-search aspect-square max-w-full">
              <Avatar className="h-full w-full aspect-square rounded-lg">
                <AvatarImage
                  className="aspect-square h-full w-full rounded-card object-cover"
                  src={petPhotoPath ?? undefined}
                  alt={pet.name}
                />
                <AvatarFallback className="!rounded-card aspect-square">
                  <CustomIcons name="paws" className="h-16 w-16 md:h-24 md:w-24" />
                </AvatarFallback>
              </Avatar>
            </div>
          )}

          {/* Conteúdo */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-3">
              <Tabs
                value={resolvedActiveTab}
                onValueChange={(value) =>
                  navigate(`/patients/pets/${pet.id}/${value}`, { replace: true, state: location.state })
                }
              >
                <div className="overflow-x-auto">
                <TabsList className="bg-muted/50 p-1 rounded-full inline-flex w-max">
                  <TabsTrigger
                    value="overview"
                    className={cn(
                      "rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm",
                      "data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground",
                    )}
                  >
                    {t("petProfile.tabs.basicInfo")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="history"
                    className={cn(
                      "rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm",
                      "data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground",
                    )}
                  >
                    {t("petProfile.tabs.history")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="upcoming"
                    className={cn(
                      "rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm",
                      "data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground",
                    )}
                  >
                    {t("petProfile.tabs.appointments")}
                  </TabsTrigger>
                  {canViewPackagesTab ? (
                    <TabsTrigger
                      value="packages"
                      className={cn(
                        "rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm",
                        "data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground",
                      )}
                    >
                      {t(hasDaycare ? "petProfile.tabs.packagesWithPlans" : "petProfile.tabs.packages")}
                    </TabsTrigger>
                  ) : null}
                  {canViewPatientFinancial ? (
                    <TabsTrigger
                      value="financial"
                      className={cn(
                        "rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm",
                        "data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground",
                      )}
                    >
                      {t("petProfile.tabs.financial")}
                    </TabsTrigger>
                  ) : null}
                </TabsList>
                </div>

                <TabsContent value="overview" className="mt-4">
                  <CardContent className="space-y-6 px-0 md:px-6">
                    {/* Resumo do animal */}
                    <div className="space-y-4">
                      {overviewFields.length > 0 ? (
                        <div className="grid gap-4 sm:grid-cols-2">
                          {overviewFields.map((field) => (
                            <div key={field.label} className="space-y-1 text-sm">
                              <span className="font-semibold block text-muted-foreground">{field.label}</span>
                              <span className="block text-foreground">{field.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {fullWidthFields.map((field) => (
                        <div key={field.label} className="space-y-1 text-sm">
                          <span className="font-semibold block text-muted-foreground">{field.label}</span>
                          <span className="block whitespace-pre-wrap break-words text-foreground">{field.value}</span>
                        </div>
                      ))}
                    </div>

                    <Separator />

                    {/* Saúde rápida */}
                    {petAllergies.length > 0 || petTemperaments.length > 0 ? (
                      <div className="space-y-3">
                        <div className="grid gap-4 lg:grid-cols-2">
                          <div className="rounded-lg border p-4 border-primary/40 bg-primary/10">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="h-4 w-4 mt-0.5 text-primary" />
                              <div>
                                <p className="text-base font-semibold text-foreground">{t("petProfile.fields.allergies")}</p>
                                <div className="mt-3 space-y-2">
                                  {petAllergies.filter((item) => item.condition?.name).length ? (
                                    <div className="flex flex-wrap gap-2">
                                      {petAllergies
                                        .filter((item) => item.condition?.name)
                                        .map((item) => (
                                          <Badge key={item.condition?.id ?? item.id}>
                                            {item.condition.name}
                                          </Badge>
                                        ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">{t("petProfile.fields.noAllergies")}</p>
                                  )}
                                </div>
                              </div>
                            </div>

                          </div>

                          <div className="rounded-lg border p-4">
                            <p className="text-base font-semibold text-foreground">{t("petProfile.fields.temperament")}</p>
                            <div className="mt-3 space-y-2">
                              {petTemperaments.filter((item) => item.condition?.name).length ? (
                                <div className="flex flex-wrap gap-2">
                                  {petTemperaments
                                    .filter((item) => item.condition?.name)
                                    .map((item) => (
                                      <Badge key={item.condition?.id ?? item.id} variant="secondary">
                                        {item.condition.name}
                                      </Badge>
                                    ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">{t("petProfile.fields.noTemperament")}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {pet.notes?.trim() && (
                      <div className="rounded-lg border bg-muted/30 p-4">
                        <p className="text-base font-semibold text-foreground">{t("petProfile.fields.notes")}</p>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                          {pet.notes}
                        </p>
                      </div>
                    )}

                    <PetDaycareHotelSummary petId={pet.id} />

                    <div className={cn("grid grid-cols-1 gap-6", hasWeightSummary && "md:grid-cols-2")}>

                      {hasWeightSummary && (
                        <div className="grid gap-4 grid-cols-1">
                          <div className="rounded-lg border p-4">
                            <p className="flex items-center gap-2 text-base font-semibold text-foreground">
                              <Weight className="h-4 w-4" />
                              {t("petProfile.fields.weight")}
                            </p>
                            <div className="mt-3 grid gap-3 text-sm">
                              {currentWeightValue && (
                                <div className="flex items-center justify-between">
                                  <span className="text-muted-foreground">{t("petProfile.fields.currentWeight")}</span>
                                  <span className="font-medium">
                                    {currentWeightValue} kg
                                  </span>
                                </div>
                              )}
                              {lastWeight?.measured_at && (
                                <div className="flex items-center justify-between">
                                  <span className="text-muted-foreground">{t("petProfile.fields.lastMeasurement")}</span>
                                  <span className="font-medium">
                                    {formatDate(lastWeight.measured_at)}
                                  </span>
                                </div>
                              )}
                              {weightChartData.length >= 2 ? (
                                <div className="mt-2">
                                  <ChartContainer
                                    config={{
                                      weight: { label: t("petProfile.fields.weightChartLabel"), color: "hsl(var(--primary))" },
                                    }}
                                    className="h-[220px] w-full aspect-auto"
                                  >
                                    <LineChart
                                      data={weightChartData}
                                      // Add right padding so last point/label doesn't clip
                                      margin={{ left: 0, right: 18, top: 8, bottom: 0 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                      <XAxis
                                        dataKey="date"
                                        tickMargin={8}
                                        minTickGap={12}
                                        padding={{ left: 6, right: 10 }}
                                      />
                                      <YAxis width={32} />
                                      <ChartTooltip content={<ChartTooltipContent />} />
                                      <Line
                                        type="monotone"
                                        dataKey="weight"
                                        stroke="hsl(var(--primary))"
                                        strokeWidth={2}
                                        dot={{ fill: "hsl(var(--primary))", r: 3 }}
                                        activeDot={{ r: 4 }}
                                      />
                                    </LineChart>
                                  </ChartContainer>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  {weightChartData.length === 1
                                    ? t("petProfile.fields.singleWeight")
                                    : t("petProfile.fields.noWeight")}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <PetWeightHistoryCard
                        petId={pet.id}
                        weights={petWeights}
                        clearConsultationWeight={handleClearConsultationWeight}
                      />
                    </div>

                    {/* Observações (destaque) */}




                    {/* Responsável */}
                    {(pet.owner?.name || ownerPrimaryPhone || ownerPrimaryEmail || ownerAddresses.length > 0 || pet.owner?.notes?.trim()) && (
                      <div className="rounded-lg border p-4">
                        <div className="flex md:flex-row flex-col gap-2 md:items-center justify-between">
                          <p className="flex items-center gap-2 text-base font-semibold text-foreground">
                            <User className="h-4 w-4" />
                            {t("petProfile.fields.owner")}
                          </p>
                          {pet.owner?.id ? (
                            <Button variant="outline" size="sm" asChild>
                              <Link to={`/patients/owners/${pet.owner.id}`}>{t("petProfile.fields.viewOwner")}</Link>
                            </Button>
                          ) : null}
                        </div>
                        <div className="mt-3 space-y-3 text-sm">
                          {pet.owner?.name && (
                            <div>
                              <p className="text-lg font-semibold">{pet.owner.name}</p>
                            </div>
                          )}
                          <div className="space-y-2 text-muted-foreground">
                            {ownerPrimaryPhone ? (
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                {ownerPrimaryPhone.is_whatsapp ? (
                                  <WhatsAppPhoneLink
                                    phoneNumber={ownerPrimaryPhone.phone_number}
                                    className="min-w-0"
                                    textClassName="truncate text-muted-foreground hover:text-primary"
                                  />
                                ) : (
                                  <span>{ownerPrimaryPhone.phone_number}</span>
                                )}
                              </div>
                            ) : null}
                            {ownerPrimaryEmail ? (
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                <span>{ownerPrimaryEmail.email}</span>
                              </div>
                            ) : null}
                            {ownerAddresses.length ? (
                              <div className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 mt-0.5" />
                                <div>
                                  {ownerAddresses.map((address) => {
                                    const addressParts = [
                                      address.street,
                                      address.number,
                                      address.complement,
                                      address.neighborhood,
                                      address.city?.name,
                                      address.city?.state?.name,
                                      address.zip_code,
                                    ].filter(Boolean);
                                    const addressText = addressParts.join(", ");
                                    return (
                                      <AddressLink key={address.id ?? `${address.street}-${address.number}`} addressText={addressText}>
                                        <p className="text-sm">
                                          {addressParts.length > 0 ? addressText : t("petProfile.fields.addressNotProvided")}
                                        </p>
                                      </AddressLink>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}
                            {/* Observações do responsável removidas */}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </TabsContent>

                <TabsContent value="history" className="mt-8">
                  <CardContent className="space-y-4 px-0 md:px-6">
                    {activeTab === "history" ? (
                      <PatientHistoryTimeline
                        petId={pet?.id}
                        clinicId={profile?.current_clinic_id ?? null}
                        includeGrooming={showGrooming}
                        timeScope="past"
                        hideFutureDetails
                        initialOpenEvent={initialOpenHistoryEvent}
                      />
                    ) : null}
                  </CardContent>
                </TabsContent>

                <TabsContent value="upcoming" className="mt-8">
                  <CardContent className="space-y-4 px-0 md:px-6">
                    {activeTab === "upcoming" ? (
                      <PatientFutureTab
                        petId={pet.id}
                        ownerId={pet.owner?.id ?? null}
                        clinicId={profile?.current_clinic_id ?? null}
                        includeGrooming={showGrooming}
                      />
                    ) : null}
                  </CardContent>
                </TabsContent>

                <TabsContent value="packages" className="mt-8">
                  <CardContent className="space-y-4 px-0 md:px-6">
                    {ownerId && clinicId ? (
                      <PatientPackagesPanel
                        ownerId={ownerId}
                        petId={pet.id}
                        clinicId={clinicId}
                        ownerName={pet.owner?.name ?? null}
                        petName={pet.name}
                      />
                    ) : (
                      <Card>
                        <CardContent className="py-6 text-sm text-muted-foreground">
                          {t("petProfile.packagesUnavailable")}
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </TabsContent>

                {canViewPatientFinancial ? (
                  <TabsContent value="financial" className="mt-8">
                    <PermissionGuard resource="patient_financial" action="view" showAlert={false}>
                      <CardContent className="space-y-4 px-0 md:px-6">
                        {activeTab === "financial" ? <PatientFinancialTab petId={pet.id} /> : null}
                      </CardContent>
                    </PermissionGuard>
                  </TabsContent>
                ) : null}
              </Tabs>
            </CardHeader>
          </Card>
        </div>
      </div>
      </div>{/* end desktop wrapper */}
      <ProfileDataModal
        open={showProfileDataModal}
        onOpenChange={(open) => {
          setShowProfileDataModal(open);
          if (!open) {
            setPendingStartConsult(false);
          }
        }}
        needsCRMV
        needsMAPA={false}
        needsAddress={false}
        onComplete={handleProfileDataComplete}
        contextLabel={t("petProfile.startConsultationContext")}
      />
      <AlertDialog open={isDeceasedStartConsultOpen} onOpenChange={setIsDeceasedStartConsultOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("petProfile.deceasedStartAttendanceTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("petProfile.deceasedStartAttendanceDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsDeceasedStartConsultOpen(false);
                if (pet?.id) {
                  navigate(`/patients/pets/${pet.id}/medical-records/new`);
                }
              }}
            >
              {t("startAttendance", { ns: "calendar" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PermissionGuard>
  );
};

export default ViewPet;
